import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Clock, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import SEOHead from "@/components/SEOHead";
import PostDate from "@/components/PostDate";
import { usePublishedBlogPosts } from "@/lib/cms";
import { buildBreadcrumb, buildWebPage } from "@/lib/seo/schemas";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

// Rendering all 234 posts at once pulled ~14MB of card images before the visitor
// scrolled at all (loading="lazy" only defers so much on a 35,000px page). One
// grid-full at a time keeps the initial payload small; "Load more" reveals the rest.
const PAGE_SIZE = 12;

const Blog = () => {
  const { data: blogPosts = [], isLoading, isError } = usePublishedBlogPosts();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // A new search or category is a fresh result set — start it from the top.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery, activeCategory]);

  const categories = useMemo(() => ["All", ...Array.from(new Set(blogPosts.map((post) => post.category)))], [blogPosts]);

  const filtered = useMemo(() => {
    const parse = (v?: string) => {
      const t = v ? Date.parse(v) : NaN;
      return Number.isNaN(t) ? 0 : t;
    };
    return blogPosts
      .filter((post) => {
        const matchesCategory = activeCategory === "All" || post.category === activeCategory;
        const matchesSearch = `${post.title} ${post.excerpt}`.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
      })
      .sort((a, b) => parse(b.publishedAt) - parse(a.publishedAt));
  }, [activeCategory, blogPosts, searchQuery]);

  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const remaining = filtered.length - visible.length;

  return (
    <>
      <SEOHead
        title="MVIBE Blog — Coaching, EI, Burnout & Communication"
        description="Practical articles on executive coaching, emotional intelligence, burnout, mental fitness and communication at work — from the MVIBE coaching team, Dubai."
        canonical="https://mvibeon.com/blog"
        schema={[
          buildWebPage({
            name: "MVIBE Blog",
            description: "Coaching, soft skills and leadership insights from MVIBE founder Mahirah.",
            path: "/blog",
            type: "CollectionPage",
          }),
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
          ]),
        ]}
      />

      <section className="bg-primary py-14 text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <nav className="mb-4 flex items-center justify-center gap-1.5 text-base text-primary-foreground/60">
            <Link to="/" className="transition-colors hover:text-primary-foreground">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-primary-foreground">Blog</span>
          </nav>
          <motion.h1 variants={fadeUp} initial="hidden" animate="visible" transition={{ duration: 0.6 }} className="mx-auto max-w-4xl text-3xl font-heading font-bold md:text-5xl lg:text-6xl">
            Insights for Your Growth Journey
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="visible" transition={{ duration: 0.6, delay: 0.15 }} className="mx-auto mt-3 max-w-2xl text-lg text-primary-foreground/80">
            Articles, thought leadership, and practical guidance from Mahirah.
          </motion.p>
        </div>
      </section>

      <section className="bg-background py-8">
        <div className="container mx-auto px-4">
          <div className="relative mb-4 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search articles..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`rounded-full px-3.5 py-1.5 text-base font-medium transition-colors ${
                  activeCategory === cat ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background pb-14">
        <div className="container mx-auto px-4">
          {isLoading && blogPosts.length === 0 ? (
            <div className="grid min-h-[32rem] grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" aria-label="Loading blog posts">
              {Array.from({ length: 6 }).map((_, i) => (
                <article key={`blog-page-loading-${i}`} className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                  <div className="h-44 w-full animate-pulse bg-muted" />
                  <div className="space-y-3 p-5">
                    <div className="h-6 w-24 animate-pulse rounded-full bg-muted" />
                    <div className="h-6 w-4/5 animate-pulse rounded bg-muted" />
                    <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
                    <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                  </div>
                </article>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-10 text-center text-base text-muted-foreground">
              {isError ? "Unable to load blog posts right now." : "No articles found matching your criteria."}
            </p>
          ) : (
            <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {visible.map((post, i) => (
                <motion.article
                  key={post.slug}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  whileHover={{ y: -4 }}
                  className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
                >
                  <img src={post.image} alt={post.title} loading="lazy" className="h-44 w-full object-cover" />
                  <div className="p-5">
                    <Badge className="mb-2 bg-accent text-accent-foreground">{post.category}</Badge>
                    <h2 className="mb-1.5 text-lg font-heading font-bold leading-snug text-foreground">{post.title}</h2>
                    <p className="mb-3 text-base text-muted-foreground line-clamp-2">{post.excerpt}</p>
                    <div className="mb-3 flex items-center justify-between text-sm text-muted-foreground">
                      <span>By Mahirah</span>
                      <div className="flex items-center gap-3">
                        <PostDate publishedAt={post.publishedAt} label={post.date} />
                        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{post.readTime}</span>
                      </div>
                    </div>
                    <Link to={`/blog/${post.slug}`} className="text-base font-semibold text-primary hover:underline">Continue Reading →</Link>
                  </div>
                </motion.article>
              ))}
            </div>

            <div className="mt-10 flex flex-col items-center gap-3">
              <p className="text-sm text-muted-foreground" aria-live="polite">
                Showing {visible.length} of {filtered.length} article{filtered.length === 1 ? "" : "s"}
              </p>
              {remaining > 0 && (
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                  className="rounded-full bg-primary px-6 py-2.5 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  Load more articles
                </button>
              )}
            </div>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default Blog;
